import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PermissionList } from "@/components/PermissionList";
import { api, errMsg, label } from "@/lib/api";

export const ROLES = ["SUPER_ADMIN", "SALES", "PROCUREMENT", "TECHNICIAN", "QUALITY_TESTER", "DISPATCH", "SUPPORT"];

function RoleSelect({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{label(r)}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function AddStaffDialog({ open, onClose, onSaved }) {
  const empty = { name: "", email: "", role: "SALES", password: "", mustChangePassword: true, repCode: "" };
  const [f, setF] = useState(empty);
  const save = async () => {
    try {
      await api.post("/admin/users", f);
      toast.success(`${f.name} added`);
      setF(empty);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="add-staff-dialog">
        <DialogHeader><DialogTitle>Add staff member</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label>Work email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email-input" /></div>
          <div className="space-y-1.5"><Label>Role</Label><RoleSelect value={f.role} onChange={(v) => setF({ ...f, role: v })} /></div>
          <div className="space-y-1.5"><Label>Sales Rep Code</Label><Input maxLength={3} placeholder="e.g. SG" value={f.repCode} onChange={(e) => setF({ ...f, repCode: e.target.value.toUpperCase() })} data-testid="staff-repcode-input" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Min. 8 characters" data-testid="staff-password-input" /></div>
          <label className="col-span-2 flex items-center justify-between rounded-lg border p-3 text-sm">
            <span><span className="font-medium">Temporary password</span><span className="block text-xs text-muted-foreground">Staff must set a new password on first login</span></span>
            <Switch checked={f.mustChangePassword} onCheckedChange={(v) => setF({ ...f, mustChangePassword: v })} data-testid="staff-must-change-toggle" />
          </label>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name || !f.email || f.password.length < 8} data-testid="staff-create-button">Add staff</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditStaffDialog({ staff, roles, catalog, me, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (staff) setF({ name: staff.name, role: staff.role, repCode: staff.repCode || "", grant: [...staff.permOverrides.grant], revoke: [...staff.permOverrides.revoke] });
  }, [staff]);
  if (!staff || !f) return null;
  const base = new Set(roles.find((r) => r.role === f.role)?.permissions || []);
  const admin = f.role === "SUPER_ADMIN";
  const isOn = (a) => admin || ((base.has(a) || f.grant.includes(a)) && !f.revoke.includes(a));
  const toggle = (a, v) => {
    const grant = f.grant.filter((x) => x !== a);
    const revoke = f.revoke.filter((x) => x !== a);
    if (v && !base.has(a)) grant.push(a);
    if (!v && base.has(a)) revoke.push(a);
    setF({ ...f, grant, revoke });
  };
  const save = async () => {
    try {
      await api.patch(`/admin/users/${staff.id}`, f);
      toast.success("Staff member updated");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-staff-dialog">
        <DialogHeader><DialogTitle>Edit {staff.name}</DialogTitle><DialogDescription>{staff.email}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-staff-name-input" /></div>
          <div className="space-y-1.5"><Label>Role</Label><RoleSelect value={f.role} onChange={(v) => setF({ ...f, role: v })} /></div>
          <div className="space-y-1.5"><Label>Sales Rep Code</Label><Input maxLength={3} placeholder="e.g. SG" value={f.repCode} onChange={(e) => setF({ ...f, repCode: e.target.value.toUpperCase() })} data-testid="edit-staff-repcode-input" /></div>
        </div>
        {f.role !== staff.role && <div className="text-xs text-amber-600" data-testid="role-change-notice">Changing the role signs this person out of active sessions.</div>}
        <div>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-sm font-semibold">Individual permissions</h3>
            <span className="text-xs text-muted-foreground">{admin ? "Super Admin has every permission" : `Overrides: +${f.grant.length} / −${f.revoke.length}`}</span>
          </div>
          <PermissionList catalog={catalog} isOn={isOn} onToggle={toggle} disabled={admin || (staff.id === me.id)} testid="user-perm"
            isOverride={(a) => f.grant.includes(a) || f.revoke.includes(a)} />
          {!admin && (f.grant.length > 0 || f.revoke.length > 0) && (
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setF({ ...f, grant: [], revoke: [] })} data-testid="clear-overrides-button">Reset to role defaults</Button>
          )}
        </div>
        <DialogFooter><Button onClick={save} data-testid="edit-staff-save">Save changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordDialog({ staff, onClose }) {
  const [pw, setPw] = useState("");
  const [must, setMust] = useState(true);
  const save = async () => {
    try {
      await api.post(`/admin/users/${staff.id}/reset-password`, { password: pw, mustChangePassword: must });
      toast.success(`Password reset for ${staff.name}`);
      setPw("");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!staff} onOpenChange={onClose}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Reset password</DialogTitle><DialogDescription>{staff?.name} will be signed out of all sessions.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label>New password</Label><Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Min. 8 characters" data-testid="reset-password-input" /></div>
        <label className="flex items-center justify-between rounded-lg border p-3 text-sm">Require change on next login
          <Switch checked={must} onCheckedChange={setMust} data-testid="reset-must-change-toggle" /></label>
        <DialogFooter><Button onClick={save} disabled={pw.length < 8} data-testid="reset-password-confirm">Reset password</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
